import React, { useCallback, useEffect, useState } from 'react';
import { message, Modal, Spin, DatePicker } from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { usePartnershipContext } from '../../../core/context/PartnershipContext';
import { fixedAssetService, FixedAsset, FixedAssetSummary } from './FixedAssetService';
import { MODAL_STYLES, MODAL_TITLE_STYLE } from '../../../styles/modalStyles';
import styles from './FixedAssets.module.css';
import dayjs from 'dayjs';

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtCurrency(n: number): string {
  if (n == null) return '$0';
  return '$' + Math.abs(n).toLocaleString('en-US');
}

function fmtNeg(n: number): string {
  if (n == null) return '$0';
  return '−$' + Math.abs(n).toLocaleString('en-US');
}

// ── Asset class badge ──────────────────────────────────────────────────────────

const CLASS_COLORS: Record<string, { bg: string; color: string }> = {
  'Machinery':            { bg: '#dbeafe', color: '#1d4ed8' },
  'Building':             { bg: '#dcfce7', color: '#166534' },
  'Vehicles':             { bg: '#fef3c7', color: '#d97706' },
  'Computers & IT':       { bg: '#f3e8ff', color: '#7c3aed' },
  'Land':                 { bg: '#f3f4f6', color: '#6b7280' },
  'Furniture & Fixtures': { bg: '#fff7ed', color: '#ea580c' },
  'Intangibles':          { bg: '#fdf2f8', color: '#db2777' },
};

const ASSET_CLASSES = Object.keys(CLASS_COLORS);
const DEPR_METHODS = ['MACRS 7-yr', 'MACRS 39-yr', 'MACRS 5-yr', 'Bonus 100%', 'Straight-line', 'Section 179', 'Non-depreciable'];

function ClassBadge({ name, disposed }: { name: string; disposed?: boolean }) {
  if (disposed) {
    return (
      <span className={styles.badge} style={{ background: '#f3f4f6', color: '#9ca3af' }}>
        Disposed
      </span>
    );
  }
  const s = CLASS_COLORS[name] ?? { bg: '#f3f4f6', color: '#6b7280' };
  return (
    <span className={styles.badge} style={{ background: s.bg, color: s.color }}>
      {name}
    </span>
  );
}

// ── Add/Edit Modal ─────────────────────────────────────────────────────────────

interface ModalFormData {
  assetName: string;
  assetClass: string;
  depreciationMethod: string;
  inServiceDate: string;
  originalCost: string;
  tags: string[];
}

const AddEditModal: React.FC<{
  mode: 'create' | 'edit';
  initial?: FixedAsset;
  partnershipId: string;
  taxYear: string;
  onClose: () => void;
  onSaved: () => void;
}> = ({ mode, initial, partnershipId, taxYear, onClose, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<ModalFormData>({
    assetName:          initial?.assetName          ?? '',
    assetClass:         initial?.assetClass         ?? 'Machinery',
    depreciationMethod: initial?.depreciationMethod ?? 'MACRS 7-yr',
    inServiceDate:      initial?.inServiceDate      ?? '',
    originalCost:       initial != null ? String(initial.originalCost) : '',
    tags:               initial?.tags               ?? [],
  });

  const set = (k: keyof ModalFormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm(prev => ({ ...prev, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.assetName.trim()) { message.error('Asset name is required'); return; }
    if (!form.inServiceDate)    { message.error('In-service date is required'); return; }
    setSaving(true);
    try {
      const cost = parseFloat(form.originalCost.replace(/,/g, '')) || 0;
      if (mode === 'create') {
        await fixedAssetService.createAsset({
          partnershipId,
          taxYear,
          assetName:          form.assetName.trim(),
          assetClass:         form.assetClass,
          depreciationMethod: form.depreciationMethod,
          inServiceDate:      form.inServiceDate,
          originalCost:       cost,
          tags:               form.tags,
        });
      } else if (initial) {
        await fixedAssetService.updateAsset(initial.id, {
          assetName:          form.assetName.trim(),
          assetClass:         form.assetClass,
          depreciationMethod: form.depreciationMethod,
          inServiceDate:      form.inServiceDate,
          originalCost:       cost,
          tags:               form.tags,
        });
      }
      message.success(`Asset ${mode === 'create' ? 'created' : 'updated'}`);
      onSaved();
    } catch {
      message.error(`Failed to ${mode === 'create' ? 'create' : 'update'} asset`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      title={<span style={MODAL_TITLE_STYLE}>{mode === 'create' ? 'Add Fixed Asset' : 'Edit Fixed Asset'}</span>}
      centered
      width={520}
      styles={MODAL_STYLES}
    >
      <div className={styles.formStack} style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: 4 }}>
        <div className={styles.formRow}>
          <label className={styles.formLabel}>Asset Name <span className={styles.req}>*</span></label>
          <input className={styles.formInput} value={form.assetName} onChange={set('assetName')} placeholder="e.g. Manufacturing Line A" />
        </div>
        <div className={styles.formGrid2}>
          <div className={styles.formRow}>
            <label className={styles.formLabel}>Asset Class <span className={styles.req}>*</span></label>
            <select className={styles.formSelect} value={form.assetClass} onChange={set('assetClass')}>
              {ASSET_CLASSES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className={styles.formRow}>
            <label className={styles.formLabel}>Depreciation Method <span className={styles.req}>*</span></label>
            <select className={styles.formSelect} value={form.depreciationMethod} onChange={set('depreciationMethod')}>
              {DEPR_METHODS.map(m => <option key={m}>{m}</option>)}
            </select>
          </div>
        </div>
        <div className={styles.formRow}>
          <label className={styles.formLabel}>In-Service Date <span className={styles.req}>*</span></label>
          <DatePicker
            style={{ width: '100%', height: 36, background: 'rgba(255,255,255,0.06)', borderColor: 'var(--ce-border)' }}
            value={form.inServiceDate ? dayjs(form.inServiceDate) : null}
            onChange={(d) => setForm(prev => ({ ...prev, inServiceDate: d ? d.format('YYYY-MM-DD') : '' }))}
            placeholder="Select date"
          />
        </div>
        <div className={styles.formRow}>
          <label className={styles.formLabel}>Original Cost <span className={styles.req}>*</span></label>
          <input
            className={styles.formInput}
            value={form.originalCost}
            onChange={set('originalCost')}
            placeholder="0"
            style={{ textAlign: 'right', fontFamily: 'monospace' }}
          />
        </div>
        <div className={styles.formRow}>
          <label className={styles.formLabel}>Tags</label>
          <input
            className={styles.formInput}
            value={form.tags.join(', ')}
            onChange={e => setForm(prev => ({ ...prev, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) }))}
            placeholder="e.g. 704(c) asset"
          />
        </div>
        <div className={styles.formActions}>
          <button className={styles.btnOutline} onClick={onClose} disabled={saving}>Cancel</button>
          <button className={styles.btnPrimary} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : mode === 'create' ? 'Save Asset' : 'Update Asset'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// ── FixedAssets ────────────────────────────────────────────────────────────────

type StatusFilter = 'all' | 'active' | 'disposed';

export const FixedAssets: React.FC = () => {
  const { selectedPartnership, taxYear } = usePartnershipContext();

  const [summary, setSummary]         = useState<FixedAssetSummary | null>(null);
  const [loading, setLoading]         = useState(false);
  const [searchText, setSearchText]   = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editTarget, setEditTarget]   = useState<FixedAsset | null>(null);

  const emptyState: FixedAssetSummary = {
    totalOriginalCost: 0,
    accumulatedDepreciation: 0,
    adjustedTaxBasis: 0,
    activeCount: 0,
    disposedCount: 0,
    assets: [],
  };

  const fetchData = useCallback(async (partnershipId: string, year: string) => {
    setLoading(true);
    try {
      const data = await fixedAssetService.getAssets(partnershipId, year);
      setSummary(data);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404 || status === undefined) {
        setSummary(emptyState);
      } else {
        message.error('Failed to load fixed assets');
        setSummary(null);
      }
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedPartnership) {
      fetchData(selectedPartnership.id, taxYear);
    } else {
      setSummary(null);
    }
  }, [selectedPartnership, taxYear, fetchData]);

  const assets = summary?.assets ?? [];
  const search = searchText.toLowerCase();

  const filtered = assets.filter(a => {
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active'   && !a.isDisposed) ||
      (statusFilter === 'disposed' &&  a.isDisposed);
    const matchesSearch =
      a.assetName.toLowerCase().includes(search) ||
      a.assetClass.toLowerCase().includes(search);
    return matchesStatus && matchesSearch;
  });

  return (
    <div className={styles.pageWrap}>
      <div className={styles.pageHead}>
        <div>
          <h1 className={styles.pageTitle}>Asset Master</h1>
          {selectedPartnership && (
            <p className={styles.pageSub}>{selectedPartnership.name} · Complete register of long-term assets</p>
          )}
        </div>
        <div className={styles.headActions}>
          <button className={styles.btnOutline} onClick={() => message.info('Import coming soon')}>
            Import
          </button>
          <button className={styles.btnOutline} onClick={() => message.info('Edit coming soon')}>
            Edit
          </button>
          <button className={styles.btnPrimary} onClick={() => setShowAddModal(true)}>
            <PlusOutlined /> Add Asset
          </button>
        </div>
      </div>

      {!selectedPartnership ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>🏭</div>
          <div className={styles.emptyText}>Select a client and partnership to view fixed assets</div>
        </div>
      ) : (
        <>
          {/* KPI cards */}
          <div className={styles.kpiRow}>
            <div className={styles.kpiCard}>
              <div className={styles.kpiLabel}>Total Original Cost</div>
              <div className={styles.kpiValue}>{fmtCurrency(summary?.totalOriginalCost ?? 0)}</div>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiLabel}>Accumulated Depreciation</div>
              <div className={styles.kpiValueNeg}>{fmtNeg(summary?.accumulatedDepreciation ?? 0)}</div>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiLabel}>Adjusted Tax Basis</div>
              <div className={styles.kpiValue}>{fmtCurrency(summary?.adjustedTaxBasis ?? 0)}</div>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiLabel}>Assets in Register</div>
              <div className={styles.kpiMulti}>
                <div className={styles.kpiMultiItem}>
                  <span className={styles.kpiCount}>{summary?.activeCount ?? 0}</span>
                  <span className={styles.kpiCountLabel}>active</span>
                </div>
                <div className={styles.kpiMultiItem}>
                  <span className={styles.kpiCount}>{summary?.disposedCount ?? 0}</span>
                  <span className={styles.kpiCountLabel}>disposed</span>
                </div>
              </div>
            </div>
          </div>

          {/* Filter row */}
          <div className={styles.filterRow}>
            <div className={styles.pillGroup}>
              {(['all', 'active', 'disposed'] as StatusFilter[]).map(f => (
                <button
                  key={f}
                  className={statusFilter === f ? styles.pillActive : styles.pill}
                  onClick={() => setStatusFilter(f)}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
            <div className={styles.searchWrap}>
              <SearchOutlined className={styles.searchIcon} />
              <input
                className={styles.searchInput}
                placeholder="Search by asset name or class..."
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
              />
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className={styles.loadingState}><Spin /></div>
          ) : (
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Asset Name</th>
                    <th style={{ width: 180 }}>Asset Class</th>
                    <th style={{ width: 140 }}>Depr. Method</th>
                    <th style={{ width: 130 }}>In-Service Date</th>
                    <th style={{ width: 140, textAlign: 'right' }}>Original Cost</th>
                    <th style={{ width: 140, textAlign: 'right' }}>Accum. Depr.</th>
                    <th style={{ width: 130, textAlign: 'right' }}>Tax Basis</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={7} className={styles.emptyCell}>
                        {searchText
                          ? 'No assets match your search'
                          : 'No fixed assets recorded for this partnership. Add an asset or use Import.'}
                      </td>
                    </tr>
                  ) : (
                    filtered.map(asset => (
                      <tr
                        key={asset.id}
                        className={styles.tableRow}
                        onClick={() => setEditTarget(asset)}
                      >
                        <td>
                          <div className={asset.isDisposed ? styles.cellNameMuted : styles.cellName}>
                            {asset.assetName}
                          </div>
                          {asset.tags.length > 0 && (
                            <div className={styles.cellSub}>
                              {asset.tags.map(tag => (
                                <span key={tag} className={styles.tag704c}>{tag}</span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td>
                          <ClassBadge name={asset.assetClass} disposed={asset.isDisposed} />
                        </td>
                        <td style={{ color: asset.isDisposed ? 'var(--ce-text-muted)' : undefined }}>
                          {asset.depreciationMethod}
                        </td>
                        <td style={{ color: asset.isDisposed ? 'var(--ce-text-muted)' : undefined }}>
                          {asset.inServiceDate}
                        </td>
                        <td className={styles.cellMono} style={{ textAlign: 'right', color: asset.isDisposed ? 'var(--ce-text-muted)' : undefined }}>
                          {fmtCurrency(asset.originalCost)}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {asset.isDisposed
                            ? <span style={{ color: 'var(--ce-text-muted)', fontFamily: 'monospace' }}>—</span>
                            : <span className={styles.cellMonoNeg}>{fmtNeg(asset.accumulatedDepreciation)}</span>}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {asset.isDisposed
                            ? <span style={{ color: 'var(--ce-text-muted)', fontFamily: 'monospace' }}>—</span>
                            : asset.taxBasis === 0
                            ? <span className={styles.cellMono} style={{ color: 'var(--ce-text-muted)' }}>$0</span>
                            : <span className={styles.cellMonoAccent}>{fmtCurrency(asset.taxBasis)}</span>}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              {filtered.length > 0 && (
                <div className={styles.tableFooter}>
                  Showing {filtered.length} of {assets.length} asset{assets.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {showAddModal && selectedPartnership && (
        <AddEditModal
          mode="create"
          partnershipId={selectedPartnership.id}
          taxYear={taxYear}
          onClose={() => setShowAddModal(false)}
          onSaved={() => { setShowAddModal(false); fetchData(selectedPartnership.id, taxYear); }}
        />
      )}

      {editTarget && selectedPartnership && (
        <AddEditModal
          mode="edit"
          initial={editTarget}
          partnershipId={selectedPartnership.id}
          taxYear={taxYear}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); fetchData(selectedPartnership.id, taxYear); }}
        />
      )}
    </div>
  );
};
